import React, { useEffect, useState, useRef } from "react";
import { useKeycloakToken } from '../common/useKeycloak';
import { useNavigate } from 'react-router-dom';
import { ProviderType, buildProviderType } from "../common/ObjectTypes";
import Spinner from "../common/Spinner";
import { page_size } from "../../api/apiUtils";
import { loadProviderTypes } from '../../api/connections/loadProviderTypesNewConnection';
import { useTranslation } from "react-i18next";
import NewConnectionStepOne from "./NewConnectionStepOne";
import { useKeycloak } from "@react-keycloak/web";
import GundiV1Modal from "./GundiV1Modal";
import ConnectionProgressIndicator from "./NewConnectionProgressIndicator/ConnectionProgressIndicator";
import { trackConnectionCreationStarted, trackConnectionCreationAbandoned } from '../../analytics/analytics';
import { containerClasses, buttonClasses, textClasses } from "../common/tailwindClassnames";

export function ManageAddConnectionStepOne() {
    type FilterTypes = {
        endpoint__in: FilterTags[],
        enabled: FilterTags[],
        type__in: FilterTags[],
        owner__in: FilterTags[],
        ordering: string,
        search: string
    };
    function createFilterTypes(
        endpoint__in: FilterTags[],
        enabled: FilterTags[],
        type__in: FilterTags[],
        owner__in: FilterTags[],
        ordering: string,
        search: string
    ): FilterTypes {
        return {
            endpoint__in: endpoint__in || [],
            enabled: enabled || [],
            type__in: type__in || [],
            owner__in: owner__in || [],
            ordering: ordering || "",
            search: search || ""
        }
    }
    type FilterTags = {
        name: string,
        id: string
    };
    const { t } = useTranslation();
    const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
    const { keycloak, initialized } = useKeycloak();

    const [providerTypes, setProviderTypes] = useState<ProviderType[]>([]);
    const [reloadProviderTypes, setReloadProviderTypes] = useState(true);
    const [loading, setLoading] = useState(false);
    const [providerTypesOffset, setProviderTypesOffset] = useState<number>(0);
    const [providerTypesTotal, setProviderTypesTotal] = useState<number>(0);
    const [v1Modal, setV1Modal] = useState<boolean>(false);
    const [searchStr, setSearchStr] = useState("");
    const [debouncedSearchStr, setDebouncedSearchStr] = useState("");
    const searchTimeout = useRef<NodeJS.Timeout | null>(null);

    const [selectedProviderType, setSelectedProviderType] = useState<ProviderType>(buildProviderType());
    const [isProviderTypeSelected, setIsProviderTypeSelected] = useState(false);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [hasMoreData, setHasMoreData] = useState(true);
    const navigate = useNavigate();

    const loadProviderTypesData = async (offset: number, search_str: string, append: boolean = false) => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoading(true);

            const [provider_types, total_provider_types, error, moreData] = await loadProviderTypes(
                token,
                offset,
                search_str,
                providerTypes,
                append
            );

            if (!error) {
                setHasMoreData(moreData);

                if (provider_types.length === 0 && append) {
                    setReloadProviderTypes(false);
                } else {
                    setProviderTypes(provider_types);
                    setProviderTypesTotal(total_provider_types);
                    setReloadProviderTypes(false);
                }
            }

            setLoading(false);
            setPageLoaded(true);
        }
    };

    useEffect(() => {
        trackConnectionCreationStarted();
    }, []);

    useEffect(() => {
        if (reloadProviderTypes) loadProviderTypesData(providerTypesOffset, searchStr);
    }, [token, reloadProviderTypes]);

    useEffect(() => {
        setProviderTypesOffset(0);
        loadProviderTypesData(0, debouncedSearchStr, false);
    }, [debouncedSearchStr]);

    useEffect(() => {
        return () => {
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }
        };
    }, []);

    const handleSelectProviderType = (pt: ProviderType) => {
        if (pt.id === "") {
            setSelectedProviderType(pt);
            setV1Modal(true);
        } else {
            setIsProviderTypeSelected(true);
            setSelectedProviderType(pt);
        }
    };

    const handleCloseModal = () => {
        setV1Modal(false);
    };

    const handleChangeProviderTypeSearchBar = (e: any) => {
        const value = e.target.value;
        setSearchStr(value);

        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        if (value.length >= 3 || value.length === 0) {
            searchTimeout.current = setTimeout(() => {
                setDebouncedSearchStr(value);
            }, 500);
        }
    };

    const handleProviderTypePageClick = () => {
        if (loading || !hasMoreData) return;

        const newOffset = providerTypesOffset + page_size;
        setProviderTypesOffset(newOffset);
        loadProviderTypesData(newOffset, searchStr, true);
    };

    const handleClickNext = () => {
        navigate(`/connections/new/configureprovider/${selectedProviderType.value}`)
    };

    const handleCancel = () => {
        trackConnectionCreationAbandoned(1);
        navigate("/")
    };

    return !pageLoaded ?
        (
            <Spinner />
        ) : (
            <div className={containerClasses.page}>
                {v1Modal &&
                    <GundiV1Modal
                        helpCenterUrl={selectedProviderType.help_center_url || ""}
                        onToggleModal={handleCloseModal}
                    />
                }
                <div className={containerClasses.header}>
                    <div className="flex flex-col">
                        <div>
                            <h1 className={textClasses.heading}>{t("Create a Connection")}</h1>
                        </div>
                    </div>
                </div>
                <div className="px-12 pt-5 -mt-5 -mx-5 xl:-mx-12 transition-colors duration-300">
                    <ConnectionProgressIndicator
                        stepZeroStatus="in-progress"
                        stepOneStatus="upcoming"
                        stepTwoStatus="upcoming"
                        stepThreeStatus="upcoming"
                    />
                    <NewConnectionStepOne
                        providerTypes={providerTypes}
                        reloadProviderTypes={reloadProviderTypes}
                        loading={loading}
                        onSelectProviderType={handleSelectProviderType}
                        onSearchProviderType={handleChangeProviderTypeSearchBar}
                        onPageClickProviderTypes={handleProviderTypePageClick}
                        providerTypesTotal={providerTypesTotal}
                        providerTypesOffset={providerTypesOffset}
                        pageLoaded={pageLoaded}
                        hasMoreData={hasMoreData}
                    />
                </div>

                <div className="fixed flex flex-row bottom-0 right-0 pr-5 py-3 pl-16 bg-white/10 backdrop-blur w-full shadow-lg transition-colors duration-300">
                    <button
                        className={`px-8 py-2 font-bold rounded mr-3 transition-colors duration-300 ${!isProviderTypeSelected
                                ? "bg-gray-lines text-white"
                                : buttonClasses.primary
                            }`}
                        onClick={() => handleClickNext()}
                        disabled={!isProviderTypeSelected}
                    >
                        {currentStep === 3 ? t("Finish") : t("Next")}
                    </button>
                    <button
                        className={buttonClasses.secondary}
                        type="button"
                        onClick={handleCancel}
                    >
                        {t("Cancel")}
                    </button>
                </div>
            </div>
        )
}